import { useMemo } from "react";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Stethoscope,
  LayoutDashboard,
  FileText,
  Globe,
  MessageSquare,
  Settings as SettingsIcon,
  History,
  Shield,
  LogOut,
  Loader2,
  GitCommit,
} from "lucide-react";
import { format } from "date-fns";
import { ThemeToggle } from "@/components/ThemeToggle";
import { SEO } from "@/components/SEO";

type ChangeLogEntry = {
  hash: string;
  shortHash: string;
  author: string;
  date: string;
  subject: string;
  body: string;
  category: string;
};

type ChangeLogResponse = {
  entries: ChangeLogEntry[];
  error?: string;
};

const CATEGORY_ORDER = [
  "Features",
  "Bug Fixes",
  "Improvements",
  "Documentation",
  "Chores",
  "Other",
];

export default function ChangeLog() {
  const { user, logout, isLoading: authLoading } = useAuth();

  const { data, isLoading, isError } = useQuery<ChangeLogResponse>({
    queryKey: ["/api/change-log"],
  });

  const grouped = useMemo(() => {
    const map = new Map<string, ChangeLogEntry[]>();
    for (const entry of data?.entries ?? []) {
      const arr = map.get(entry.category) ?? [];
      arr.push(entry);
      map.set(entry.category, arr);
    }
    return map;
  }, [data]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  if (!user) {
    window.location.href = "/api/login";
    return null;
  }

  const entries = data?.entries ?? [];
  const apiError = data?.error;

  return (
    <div className="h-screen flex flex-col bg-background">
      <SEO title="Change log — LRScribe" noindex />
      <header className="h-16 border-b border-border bg-card flex items-center justify-between px-6 shrink-0">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Stethoscope className="w-5 h-5 text-primary" />
          </div>
          <span className="font-heading font-bold text-lg text-foreground">
            LRScribe
          </span>
        </div>
        <nav className="flex items-center gap-6">
          <Link
            href="/dashboard"
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
          >
            <LayoutDashboard className="w-4 h-4" />
            Dashboard
          </Link>
          <Link
            href="/templates"
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
          >
            <FileText className="w-4 h-4" />
            Templates
          </Link>
          <Link
            href="/rules"
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
          >
            <Globe className="w-4 h-4" />
            Rules
          </Link>
          <Link
            href="/chat"
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
          >
            <MessageSquare className="w-4 h-4" />
            Chat
          </Link>
        </nav>
        <div className="flex items-center gap-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-3 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                data-testid="button-user-menu"
              >
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user.profileImageUrl || undefined} />
                  <AvatarFallback className="bg-primary/10 text-primary text-sm">
                    {user.firstName?.charAt(0) || user.email?.charAt(0) || "U"}
                  </AvatarFallback>
                </Avatar>
                <span
                  className="text-sm font-medium text-foreground hidden sm:block"
                  data-testid="text-user-name"
                >
                  {user.firstName
                    ? `${user.firstName} ${user.lastName || ""}`.trim()
                    : user.email}
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem asChild>
                <Link href="/settings" data-testid="link-settings">
                  <SettingsIcon className="w-4 h-4" />
                  Settings
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link href="/security" data-testid="link-security">
                  <Shield className="w-4 h-4" />
                  Security
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link href="/change-log" data-testid="link-change-log">
                  <History className="w-4 h-4" />
                  Change log
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => logout()}
                className="text-destructive focus:text-destructive"
                data-testid="button-logout"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <ThemeToggle />
        </div>
      </header>

      <div className="flex-1 overflow-auto">
        <div className="max-w-3xl mx-auto px-6 py-10 space-y-8">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Change log</h1>
            <p className="text-sm text-muted-foreground mt-1">
              The 50 most recent updates to LRScribe, grouped by category.
            </p>
          </div>

          {isLoading ? (
            <div className="space-y-6" data-testid="changelog-loading">
              {[0, 1, 2].map((i) => (
                <div key={i} className="space-y-3">
                  <div className="h-5 w-32 bg-muted rounded animate-pulse" />
                  <div className="space-y-2">
                    {[0, 1, 2].map((j) => (
                      <div
                        key={j}
                        className="h-16 bg-muted/50 border border-border rounded-lg animate-pulse"
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : isError ? (
            <div
              className="border border-border rounded-lg p-6 bg-card text-center"
              data-testid="changelog-error"
            >
              <p className="text-sm text-muted-foreground">
                Couldn't load the change log. Please try again.
              </p>
            </div>
          ) : entries.length === 0 ? (
            <div
              className="border border-border rounded-lg p-8 bg-card text-center"
              data-testid="changelog-empty"
            >
              <GitCommit className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm font-medium text-foreground">
                No changes to show yet
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {apiError ||
                  "Once commits are made to this project, they'll appear here."}
              </p>
            </div>
          ) : (
            <div className="space-y-8">
              {CATEGORY_ORDER.map((category) => {
                const items = grouped.get(category);
                if (!items || items.length === 0) return null;
                return (
                  <section
                    key={category}
                    className="space-y-3"
                    data-testid={`section-${category
                      .toLowerCase()
                      .replace(/\s+/g, "-")}`}
                  >
                    <h2 className="text-lg font-semibold text-foreground">
                      {category}
                      <span className="ml-2 text-xs font-normal text-muted-foreground">
                        ({items.length})
                      </span>
                    </h2>
                    <ul className="space-y-2">
                      {items.map((entry) => {
                        let dateLabel = "";
                        try {
                          dateLabel = format(new Date(entry.date), "MMM d, yyyy");
                        } catch {
                          dateLabel = entry.date;
                        }
                        return (
                          <li
                            key={entry.hash}
                            className="border border-border rounded-lg p-4 bg-card"
                            data-testid={`entry-commit-${entry.shortHash}`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <p
                                className="text-sm text-foreground font-medium"
                                data-testid={`text-subject-${entry.shortHash}`}
                              >
                                {entry.subject}
                              </p>
                              <code
                                className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded shrink-0 font-mono"
                                data-testid={`text-hash-${entry.shortHash}`}
                              >
                                {entry.shortHash}
                              </code>
                            </div>
                            <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
                              <span data-testid={`text-date-${entry.shortHash}`}>
                                {dateLabel}
                              </span>
                              {entry.author && (
                                <>
                                  <span>·</span>
                                  <span
                                    data-testid={`text-author-${entry.shortHash}`}
                                  >
                                    {entry.author}
                                  </span>
                                </>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
